import { useCallback, useMemo } from 'react'

import {
  ContentModuleType,
  Network,
  PostTypeContext,
  Space,
} from '@tribeplatform/gql-client/types'
import {
  useContentModuleUtils,
  useQuery,
} from '@tribeplatform/react-components/common'
import { useRouter } from '@tribeplatform/react-sdk'
import { useNetwork, useSpace } from '@tribeplatform/react-sdk/hooks'

import { ComposerProps, ComposerQueryParams } from './types.js'

interface Props {
  composerProps: ComposerProps
}

export const useComposer = ({ composerProps }: Props) => {
  const { spacePath } = composerProps
  const { push, location } = useRouter()
  const {
    post_type: postTypeIdOrSlug,
    space_id: spaceIdQuery,
    content_type: contentModuleType,
  } = useQuery<ComposerQueryParams>()
  const { data: network } = useNetwork()
  const isSpaceSet = !!spacePath || !!spaceIdQuery
  const { isPostType, isContentModule } = useContentModuleUtils()

  const { data: space } = useSpace({
    fields: {
      authMemberProps: {
        availablePostTypes: 'all',
        spaceMember: {
          role: 'basic',
        },
        permissions: 'all',
      },
      image: 'all',
    },
    variables: spaceIdQuery ? { id: spaceIdQuery } : { path: spacePath },
    useQueryOptions: { enabled: isSpaceSet },
  })

  const composableContentTypes = useMemo(() => {
    return (
      getComposableContentTypes({
        selectedSpace: space,
        network,
      }) ?? []
    )
  }, [network, space])
  const selectedContentType = useMemo(() => {
    if (postTypeIdOrSlug) {
      return composableContentTypes.find(
        postType => isPostType(postType) && postType.id === postTypeIdOrSlug,
      )
    }
    if (contentModuleType) {
      return composableContentTypes.find(
        item =>
          !isPostType(item) && item.contentModuleType === contentModuleType,
      )
    }

    return null
  }, [composableContentTypes, postTypeIdOrSlug, contentModuleType, isPostType])

  const onCancel = useCallback(() => {
    if (location?.key) {
      window?.history?.back()
    } else {
      push('/')
    }
  }, [location, push])

  const hasSingleComposableContentType = composableContentTypes.length === 1
  const shouldRenderPostForm =
    !!composerProps.postPath ||
    !!postTypeIdOrSlug ||
    (!!selectedContentType && isPostType(selectedContentType))
  /**
   * @todo: currently checking only for event content module type
   * we should check for any of the content module types
   */
  const shouldRenderContentModuleForm =
    !!composerProps.eventPath ||
    (!!selectedContentType && isContentModule(selectedContentType))
  const shouldRenderContentTypeSelector =
    !(shouldRenderPostForm || shouldRenderContentModuleForm) &&
    composerProps.new

  return {
    hasSingleComposableContentType,
    composableContentTypes,
    selectedContentType,
    shouldRenderPostForm,
    shouldRenderContentModuleForm,
    shouldRenderContentTypeSelector,
    selectedSpace: space,
    onCancel,
    postTypeSlug: postTypeIdOrSlug,
    contentModuleType,
  }
}

interface GetComposableContentTypesProps {
  selectedSpace?: Space
  network: Network
}

export const getComposableContentTypes = ({
  network,
  selectedSpace,
}: GetComposableContentTypesProps) => {
  const availableContentModules = getAvailableContentModules({
    network,
    selectedSpace,
  })

  if (selectedSpace?.id) {
    const postTypes =
      selectedSpace.authMemberProps?.availablePostTypes?.filter(
        postType => postType.context === PostTypeContext.post,
      ) ?? []

    return [...availableContentModules, ...postTypes]
  }
  const postTypes =
    network.postTypes.filter(
      postType =>
        postType.context === PostTypeContext.post &&
        postType.slug !== 'tribe-message' &&
        postType.authMemberProps?.availableSpaces?.length > 0 &&
        !postType.archived,
    ) ?? []

  return [...availableContentModules, ...postTypes]
}

const getAvailableContentModules = ({
  network,
  selectedSpace,
}: {
  network: Network
  selectedSpace?: Space
}) => {
  return network.contentModules.filter(contentModule => {
    switch (contentModule.contentModuleType) {
      case ContentModuleType.event: {
        const isEventModuleAvailable =
          network.usages?.usages?.eventContentModuleAccess
        const eventModuleCanBeCreated =
          contentModule.authMemberProps?.availableSpaces?.length > 0
        /**
         * If a space is not selected we just need to check if the event module is available on the network.
         */
        const selectedSpaceHasEventModule =
          !selectedSpace?.id ||
          selectedSpace?.authMemberProps?.availableContentModules?.includes(
            contentModule.contentModuleType,
          )
        return (
          isEventModuleAvailable &&
          eventModuleCanBeCreated &&
          selectedSpaceHasEventModule
        )
      }
      default:
        return contentModule.authMemberProps?.availableSpaces?.length > 0
    }
  })
}
